/**
 * Per-route SEO + JSON-LD helpers.
 *
 * Why a single module: page components stay clean (one `<SeoHead .../>`
 * call per route) and all canonical / hreflang / structured-data logic
 * lives in one place we can audit.
 *
 * Canonical: we use the production origin from env (`REACT_APP_BACKEND_URL`)
 * because the user's real domain (dealrating.it) IS the backend URL on
 * production. The preview URL is `*.preview.emergentagent.com` which we
 * do NOT want crawlers to index — Google would treat it as duplicate.
 *
 * Robots: in any non-production hostname we emit `noindex, nofollow` so
 * preview / staging links never compete with the live site in search.
 */
import { Helmet } from "react-helmet-async";
import { useLang } from "@/lib/lang-context";

const CANONICAL_ORIGIN = "https://dealrating.it";

const isProductionHost = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "dealrating.it" || h === "www.dealrating.it";
};

/**
 * Build a canonical-shaped URL for a given path + language.
 * - IT version → no `?lang=` (keeps the legacy short URL clean for backlinks).
 * - EN version → `?lang=en` (lets Google index the English page distinctly).
 */
const buildLangUrl = (path, lang) => {
  const base = `${CANONICAL_ORIGIN}${path}`;
  return lang === "en" ? `${base}?lang=en` : base;
};

/**
 * Generic per-page SEO head.
 *
 * Props
 * -----
 * - title:       window/tab title (string, REQUIRED) — caller usually picks
 *                between IT/EN via `useLang().lang`.
 * - description: 140-160 char summary (REQUIRED, same convention as title).
 * - path:        canonical path including leading slash. Defaults to current.
 * - lang:        OPTIONAL — overrides the lang from context. Normal callers
 *                should pass IT/EN strings via the title/description props
 *                instead and let this component pick the current lang.
 * - image:       absolute OG image URL (optional; defaults to /api/og/digest.png)
 * - type:        OG type: "website" | "article" (default "website")
 * - jsonLd:      optional JSON-LD object(s) — appended as ld+json script.
 */
export const SeoHead = ({
  title,
  description,
  path,
  lang: langProp,
  image,
  type = "website",
  jsonLd,
}) => {
  // Read the active language from context so SEO meta + hreflang stay in
  // sync with whatever the user is actually seeing. Callers can still
  // override with the `lang` prop for special cases (e.g. shared sheets
  // that are locked to a specific language by their stored payload).
  const ctx = useLang();
  const lang = langProp || ctx?.lang || "it";

  const resolvedPath =
    path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const itUrl = buildLangUrl(resolvedPath, "it");
  const enUrl = buildLangUrl(resolvedPath, "en");
  // Canonical points to the currently-rendered language variant. Both
  // variants advertise each other through `<link rel=alternate hreflang>`.
  const canonical = lang === "en" ? enUrl : itUrl;

  const ogImage = image || `${CANONICAL_ORIGIN}/api/og/digest.png`;
  const robots = isProductionHost() ? "index,follow" : "noindex,nofollow";

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="it" href={itUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={itUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Deal Rating" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={lang === "it" ? "it_IT" : "en_GB"} />
      <meta property="og:locale:alternate" content={lang === "it" ? "en_GB" : "it_IT"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Home-page JSON-LD: Organization + WebSite with SearchAction. Helps
 * Google show a sitelinks search box and unify entity knowledge.
 */
export const homeJsonLd = () => [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Deal Rating",
    url: CANONICAL_ORIGIN,
    logo: `${CANONICAL_ORIGIN}/api/og/digest.png`,
    email: "info@dealrating.it",
    areaServed: { "@type": "AdministrativeArea", name: "Salento, Puglia, Italy" },
    sameAs: [],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Deal Rating",
    url: CANONICAL_ORIGIN,
    inLanguage: ["it", "en"],
    potentialAction: {
      "@type": "SearchAction",
      target: `${CANONICAL_ORIGIN}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  },
];

/**
 * Article JSON-LD for a single shared Evaluation Sheet.
 * Surfaces the listing town, score and asking price so the SERP snippet
 * is rich (rating + price) when someone shares a /sheet/:id link.
 */
export const sheetJsonLd = (analysis, path) => {
  if (!analysis) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: analysis.title || `Valutazione immobile ${analysis.town || ""}`.trim(),
    description: analysis.economic_analysis || analysis.technical_note || "",
    datePublished: analysis.created_at,
    inLanguage: "it",
    mainEntityOfPage: { "@type": "WebPage", "@id": `${CANONICAL_ORIGIN}${path}` },
    publisher: {
      "@type": "Organization",
      name: "Deal Rating",
      logo: { "@type": "ImageObject", url: `${CANONICAL_ORIGIN}/api/og/digest.png` },
    },
    about: { "@type": "Place", name: analysis.town, addressRegion: analysis.province },
    ...(analysis.total_score != null && {
      review: {
        "@type": "Review",
        reviewRating: {
          "@type": "Rating",
          ratingValue: analysis.total_score,
          bestRating: 10,
          worstRating: 0,
        },
        author: { "@type": "Organization", name: "Deal Rating" },
      },
    }),
  };
};
